import {
	ownerRolePermissions, projectManagerRolePermissions, projectEditorRolePermissions,
	projectReviewerRolePermissions, ProjectRolePermissions
} from '@projectMembers/models/projectRolePermissions';

interface IProjectMember {
	isOwner: boolean;
	// eslint-disable-next-line no-use-before-define
	role: ProjectRole;
}

interface IProjectInvitation {
	// eslint-disable-next-line no-use-before-define
	role: ProjectRole;
}

export enum ProjectMemberRoleType {
	ProjectOwner = 'ProjectOwner',
	ProjectManager = 'ProjectManager',
	ProjectEditor = 'ProjectEditor',
	ProjectReviewer = 'ProjectReviewer'
}

export type ProjectMemberRoleJSON = {
	id: number,
	type: ProjectMemberRoleType
}

const OWNER_LEVEL = 1;
const MANAGER_LEVEL = 2;
const PROJECT_EDITOR_LEVEL = 3;
const PROJECT_REVIEWER_LEVEL = 4;

export default class ProjectRole {
	id: ProjectMemberRoleType;
	name: string;
	level: number;
	permissions: ProjectRolePermissions;
	description: string;

	constructor(
		id: ProjectMemberRoleType,
		name: string,
		level: number,
		permissions: ProjectRolePermissions,
		description = ''
	) {
		this.id = id;
		this.name = name;
		this.level = level;
		this.permissions = permissions;
		this.description = description;
	}

	get isOwner() { return this.id === ProjectRole.owner().id; }

	get isManager() { return this.id === ProjectRole.projectManager().id; }

	get isReviewer() { return this.id === ProjectRole.projectReviewer().id; }

	get projectsPermissions() { return this.permissions.projectsPermissions; }

	get assetsPermissions() { return this.permissions.assetsPermissions; }

	get tasksPermissions() { return this.permissions.tasksPermissions; }

	get commentsPermissions() { return this.permissions.commentsPermissions; }

	get usersPermissions() { return this.permissions.usersPermissions; }

	static owner() {
		return new ProjectRole(
			ProjectMemberRoleType.ProjectOwner,
			'Owner',
			OWNER_LEVEL,
			ownerRolePermissions );
	}

	static projectManager() {
		return new ProjectRole(
			ProjectMemberRoleType.ProjectManager,
			'Manager',
			MANAGER_LEVEL,
			projectManagerRolePermissions,
			'Can manage projects, people and have full sharing capabilities.'
		);
	}

	static projectEditor() {
		return new ProjectRole(
			ProjectMemberRoleType.ProjectEditor,
			'Editor',
			PROJECT_EDITOR_LEVEL,
			projectEditorRolePermissions,
			'Can upload, manage assets and only invite via email.'
		);
	}

	static projectReviewer() {
		return new ProjectRole(
			ProjectMemberRoleType.ProjectReviewer,
			'Reviewer',
			PROJECT_REVIEWER_LEVEL,
			projectReviewerRolePermissions,
			'Can comment, markup and approve assets.'
		);
	}

	static fromID( id: ProjectMemberRoleType ): ProjectRole | null {
		// eslint-disable-next-line no-use-before-define
		return AVAILABLE_ROLES_BY_TYPE[ id ] ?? null;
	}

	/* eslint-disable no-use-before-define */
	canInviteRoles() {
		if ( !this.usersPermissions.invite ) { return []; }

		return VALID_ASSIGNABLE_ROLES
			.filter( role => role.level >= this.level );
	}

	canInviteRole( role: ProjectRole ): boolean {
		return this.usersPermissions.invite && !role.isOwner && role.level >= this.level;
	}

	canBeInvitedByLink(): boolean {
		return LINK_ASSIGNABLE_ROLES.map( role => role.id ).includes( this.id );
	}

	isAtLeast( level: number ): boolean {
		return this.level <= level;
	}

	// Project Members
	canEditProjectMemberRole( member: IProjectMember ): boolean {
		return !member.isOwner
		&& this.usersPermissions.destroy
		&& ( this.hasLowerLevel( member.role ) || this.hasSameLevel( member.role ) );
	}

	canDeleteProjectMember( member: IProjectMember ): boolean {
		return !member.isOwner
		&& this.usersPermissions.destroy
		&& ( this.hasLowerLevel( member.role ) || this.hasSameLevel( member.role ) );
	}

	canDeleteProjectInvite( invite: IProjectInvitation ): boolean {
		return this.usersPermissions.destroy
		&& ( this.hasLowerLevel( invite.role ) || this.hasSameLevel( invite.role ) );
	}

	hasLowerLevel( role: ProjectRole ): boolean {
		return role && this.level < role.level;
	}

	hasSameLevel( role: ProjectRole ): boolean {
		return role && this.level === role.level;
	}
}

// The roles here are ordered by hierarchy
export const AVAILABLE_ROLES: ProjectRole[] = [
	ProjectRole.owner(),
	ProjectRole.projectManager(),
	ProjectRole.projectEditor(),
	ProjectRole.projectReviewer()
];

export const VALID_ASSIGNABLE_ROLES = [
	ProjectRole.projectManager(),
	ProjectRole.projectEditor(),
	ProjectRole.projectReviewer()
];

export const LINK_ASSIGNABLE_ROLES = [
	ProjectRole.projectEditor(),
	ProjectRole.projectReviewer()
];

export const AVAILABLE_ROLES_BY_TYPE = Object.freeze( {
	[ ProjectRole.owner().id ]: ProjectRole.owner(),
	[ ProjectRole.projectManager().id ]: ProjectRole.projectManager(),
	[ ProjectRole.projectEditor().id ]: ProjectRole.projectEditor(),
	[ ProjectRole.projectReviewer().id ]: ProjectRole.projectReviewer()
} );

export const AVAILABLE_ROLES_NAMES_BY_KEY = Object.freeze( {
	[ ProjectRole.owner().id ]: 'Project Owner',
	[ ProjectRole.projectManager().id ]: ProjectRole.projectManager().name,
	[ ProjectRole.projectEditor().id ]: ProjectRole.projectEditor().name,
	[ ProjectRole.projectReviewer().id ]: ProjectRole.projectReviewer().name
} );
